import { useState, useEffect } from 'react';
import type { CSSProperties } from 'react';
import {
  useEditor,
  useEditorState,
  useKeyboardShortcuts,
  ADD_LAYER,
  UPDATE_PROPERTY,
  getCompositionProps,
} from '@/lib/kinema';
import type { ToolManager } from '@/lib/kinema';
import { MediaLibraryModal } from '../panels/MediaLibraryModal';
import { useTranscode } from '../transcodeContext';

/**
 * Pre-generated id so we can patch the layer later (e.g. when the source's
 * real duration becomes known via `durationchange`).
 */
function generateMediaLayerId(): string {
  return `layer-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Picks an initial `endTime` for a newly-added media layer. Falls back to the
 * composition's duration when the source duration isn't available — this
 * happens for VBR MP3s without Xing headers and for some streaming videos at
 * `loadedmetadata` time.
 */
function resolveMediaEndTime(sourceDuration: number, compDuration: number): number {
  if (Number.isFinite(sourceDuration) && sourceDuration > 0) {
    return Math.min(sourceDuration, compDuration);
  }
  return compDuration;
}

export function FloatingToolbar({ toolManager }: { toolManager: ToolManager | null }) {
  const editor = useEditor();
  const state = useEditorState();
  const [activeTool, setActiveTool] = useState<string>('select');
  const [mediaModal, setMediaModal] = useState<'image' | 'video' | 'audio' | null>(null);
  const { transcodingLayers, transcodeMovForLayer } = useTranscode();

  const switchTool = (name: string) => {
    if (!toolManager) return;
    toolManager.setActiveTool(name);
    setActiveTool(name);
  };

  useEffect(() => {
    if (!toolManager) return;
    const interval = setInterval(() => {
      const current = toolManager.getActiveTool().name;
      if (current !== activeTool) setActiveTool(current);
    }, 100);
    return () => clearInterval(interval);
  }, [toolManager, activeTool]);

  useKeyboardShortcuts(editor, { toolManager });

  const comp = getCompositionProps(state);
  const centerX = comp.width / 2;
  const centerY = comp.height / 2;

  const addLayer = (type: string) => {
    const names: Record<string, string> = {
      shape: 'Shape', text: 'Text', image: 'Image', video: 'Video',
    };
    const w = type === 'text' ? 200 : 150;
    const h = type === 'text' ? 50 : 150;
    editor.dispatchCommand(ADD_LAYER, {
      type,
      name: `${names[type] ?? type} ${Object.keys(state.layers).length}`,
      width: w,
      height: h,
      x: centerX - w / 2,
      y: centerY - h / 2,
      ...(type === 'shape' && { fill: '#845adf' }),
      ...(type === 'text' && { text: 'Hello World', fontSize: 32, color: '#000000' }),
    });
  };

  const addShapeLayer = (shape: string) => {
    const w = 150;
    const h = shape === 'ellipse' ? 100 : 150;
    editor.dispatchCommand(ADD_LAYER, {
      type: 'shape',
      name: `${shape.charAt(0).toUpperCase() + shape.slice(1)} ${Object.keys(state.layers).length}`,
      width: w,
      height: h,
      x: centerX - w / 2,
      y: centerY - h / 2,
      shape,
      fill: '#845adf',
    });
  };

  const selectIcon = (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <path d="M2 1L13 7.5L7.5 9L6 14.5L2 1Z" />
    </svg>
  );
  const panIcon = (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M8 2v12M2 8h12M8 2l-2 2M8 2l2 2M8 14l-2-2M8 14l2-2M2 8l2-2M2 8l2 2M14 8l-2-2M14 8l-2 2" />
    </svg>
  );
  const textIcon = (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <path d="M3 3h10v2h-4v8H7V5H3V3z" />
    </svg>
  );
  const imageIcon = (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="3" width="12" height="10" rx="1" />
      <circle cx="5.5" cy="6.5" r="1.5" />
      <path d="M2 11l3-3 2 2 3-4 4 5" />
    </svg>
  );
  const videoIcon = (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="1" y="3" width="10" height="10" rx="1" />
      <path d="M11 6l4-2v8l-4-2" />
    </svg>
  );
  const audioIcon = (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <path d="M2 8h1.5M5 5.5v5M7.5 3.5v9M10 5v6M12.5 6.5v3" />
    </svg>
  );
  const penIcon = (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M2 14l1.5-5L12 2l2 2L5.5 12.5z" />
      <path d="M10 4l2 2" />
    </svg>
  );
  const undoIcon = (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M4 7h6a3 3 0 0 1 0 6H8" />
      <path d="M7 4L4 7l3 3" />
    </svg>
  );
  const redoIcon = (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 7H6a3 3 0 0 0 0 6h2" />
      <path d="M9 4l3 3-3 3" />
    </svg>
  );

  const toolBtn = (name: string, icon: React.ReactNode, title: string, key?: string) => (
    <button
      onClick={() => switchTool(name)}
      title={title}
      style={{
        ...floatingBtnStyle,
        position: 'relative' as const,
        background: activeTool === name ? '#2563eb' : 'transparent',
        color: activeTool === name ? '#fff' : '#475569',
      }}
    >
      {icon}
      {key && activeTool !== name && (
        <span
          style={{
            position: 'absolute',
            bottom: 1,
            right: 2,
            fontSize: 8,
            fontWeight: 600,
            lineHeight: 1,
            opacity: 0.5,
          }}
        >
          {key}
        </span>
      )}
    </button>
  );

  const toolbar = (
    <div
      style={{
        position: 'absolute',
        top: 12,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        padding: '4px 6px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
      }}
    >
      {toolBtn('select', selectIcon, 'Select (V)', 'V')}
      {toolBtn('pan', panIcon, 'Pan (H)', 'H')}
      {toolBtn('pen', penIcon, 'Pen (P)', 'P')}

      <Divider />

      <ShapeSubmenu onAddShape={(shape) => addShapeLayer(shape)} />
      <button onClick={() => addLayer('text')} title="Text" style={floatingBtnStyle}>
        {textIcon}
      </button>
      <button onClick={() => setMediaModal('image')} title="Image" style={floatingBtnStyle}>
        {imageIcon}
      </button>
      <button onClick={() => setMediaModal('video')} title="Video" style={floatingBtnStyle}>
        {videoIcon}
      </button>
      <button onClick={() => setMediaModal('audio')} title="Audio" style={floatingBtnStyle}>
        {audioIcon}
      </button>

      <Divider />

      <button
        onClick={() => editor.undo()}
        disabled={!editor.canUndo()}
        title="Undo (Cmd+Z)"
        style={{ ...floatingBtnStyle, opacity: editor.canUndo() ? 1 : 0.3 }}
      >
        {undoIcon}
      </button>
      <button
        onClick={() => editor.redo()}
        disabled={!editor.canRedo()}
        title="Redo (Cmd+Shift+Z)"
        style={{ ...floatingBtnStyle, opacity: editor.canRedo() ? 1 : 0.3 }}
      >
        {redoIcon}
      </button>
    </div>
  );

  return (
    <>
      {toolbar}
      {transcodingLayers.size > 0 && (
        <div
          style={{
            position: 'absolute',
            bottom: 12,
            left: 12,
            zIndex: 100,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          {Array.from(transcodingLayers.entries()).map(([layerId, percent]) => (
            <div
              key={layerId}
              style={{
                background: '#1e293b',
                color: '#e2e8f0',
                borderRadius: 6,
                padding: '6px 12px',
                fontSize: 11,
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }}>
                <circle cx="7" cy="7" r="5.5" fill="none" stroke="#475569" strokeWidth="2" />
                <path d="M7 1.5A5.5 5.5 0 0 1 12.5 7" fill="none" stroke="#845adf" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Converting video... {Math.round(percent)}%
            </div>
          ))}
        </div>
      )}
      {mediaModal && (
        <MediaLibraryModal
          fileType={mediaModal}
          onSelect={(media) => {
            const src = media.storage_uri || '';
            const name = media.name || `${mediaModal} ${Object.keys(state.layers).length}`;

            if (mediaModal === 'image' && src) {
              const img = new Image();
              img.crossOrigin = 'anonymous';
              img.onload = () => {
                let w = img.naturalWidth;
                let h = img.naturalHeight;
                const maxDim = 400;
                if (w > maxDim || h > maxDim) {
                  const ratio = Math.min(maxDim / w, maxDim / h);
                  w = Math.round(w * ratio);
                  h = Math.round(h * ratio);
                }
                editor.dispatchCommand(ADD_LAYER, {
                  type: 'image', name, src, width: w, height: h,
                  x: centerX - w / 2, y: centerY - h / 2,
                });
              };
              img.onerror = () => {
                editor.dispatchCommand(ADD_LAYER, {
                  type: 'image', name, src, width: 300, height: 200,
                  x: centerX - 150, y: centerY - 100,
                });
              };
              img.src = src;
            } else if (mediaModal === 'video' && src) {
              const layerId = generateMediaLayerId();
              const vid = document.createElement('video');
              vid.preload = 'auto';
              let added = false;
              const dispatchAdd = (w: number, h: number, endTime: number) => {
                if (added) return;
                added = true;
                editor.dispatchCommand(ADD_LAYER, {
                  id: layerId,
                  type: 'video', name, src, width: w, height: h,
                  x: centerX - w / 2, y: centerY - h / 2,
                  startTime: 0, endTime,
                });
              };
              vid.onloadedmetadata = () => {
                let w = vid.videoWidth || 300;
                let h = vid.videoHeight || 200;
                const maxDim = 400;
                if (w > maxDim || h > maxDim) {
                  const ratio = Math.min(maxDim / w, maxDim / h);
                  w = Math.round(w * ratio);
                  h = Math.round(h * ratio);
                }
                dispatchAdd(w, h, resolveMediaEndTime(vid.duration, comp.duration));
              };
              // Some VBR / streaming sources only resolve a real duration on
              // `durationchange`, after the file has been scanned further.
              // Patch endTime in-place once we know.
              vid.ondurationchange = () => {
                if (!added || !Number.isFinite(vid.duration) || vid.duration <= 0) return;
                editor.dispatchCommand(UPDATE_PROPERTY, {
                  layerId,
                  property: 'endTime',
                  value: Math.min(vid.duration, comp.duration),
                });
              };
              vid.onerror = () => dispatchAdd(300, 200, comp.duration);
              vid.src = src;
            } else if (mediaModal === 'audio' && src) {
              const layerId = generateMediaLayerId();
              const aud = document.createElement('audio');
              aud.preload = 'auto';
              let added = false;
              const dispatchAdd = (endTime: number) => {
                if (added) return;
                added = true;
                editor.dispatchCommand(ADD_LAYER, {
                  id: layerId,
                  type: 'audio', name, src,
                  width: 0, height: 0, x: 0, y: 0,
                  startTime: 0, endTime,
                });
              };
              aud.onloadedmetadata = () => dispatchAdd(resolveMediaEndTime(aud.duration, comp.duration));
              // VBR MP3s without Xing headers report Infinity/NaN at
              // loadedmetadata and only resolve a real duration on
              // durationchange. Patch endTime once we know.
              aud.ondurationchange = () => {
                if (!added) {
                  dispatchAdd(resolveMediaEndTime(aud.duration, comp.duration));
                  return;
                }
                if (!Number.isFinite(aud.duration) || aud.duration <= 0) return;
                editor.dispatchCommand(UPDATE_PROPERTY, {
                  layerId,
                  property: 'endTime',
                  value: Math.min(aud.duration, comp.duration),
                });
              };
              aud.onerror = () => dispatchAdd(comp.duration);
              aud.src = src;
            } else {
              editor.dispatchCommand(ADD_LAYER, {
                type: mediaModal, name, src, width: 300, height: 200,
                x: centerX - 150, y: centerY - 100,
              });
            }
            setMediaModal(null);
          }}
          onMovUpload={async (media) => {
            const name = media.name || `Video ${Object.keys(state.layers).length}`;
            const w = 300;
            const h = 200;

            editor.dispatchCommand(ADD_LAYER, {
              type: 'video', name, src: '', width: w, height: h,
              x: centerX - w / 2, y: centerY - h / 2,
            });

            const layers = Object.values(editor.getState().layers);
            const placeholderLayer = layers[layers.length - 1];
            if (!placeholderLayer) return;

            transcodeMovForLayer(placeholderLayer.id, media.storage_uri!, name);
            setMediaModal(null);
          }}
          onClose={() => setMediaModal(null)}
        />
      )}
    </>
  );
}

function ShapeSubmenu({ onAddShape }: { onAddShape: (shape: string) => void }) {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setOpen(!open)} title="Shape" style={floatingBtnStyle}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="2" y="3" width="12" height="10" rx="1" />
        </svg>
        <svg width="6" height="6" viewBox="0 0 6 6" fill="currentColor" style={{ marginLeft: 2 }}>
          <path d="M0 1.5l3 3 3-3z" />
        </svg>
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: 4,
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 6,
            padding: '4px 0',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            zIndex: 200,
            minWidth: 120,
          }}
        >
          {[
            {
              shape: 'rect',
              label: 'Rectangle',
              icon: (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2">
                  <rect x="1" y="2" width="12" height="10" rx="1" />
                </svg>
              ),
            },
            {
              shape: 'ellipse',
              label: 'Ellipse',
              icon: (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2">
                  <ellipse cx="7" cy="7" rx="6" ry="4.5" />
                </svg>
              ),
            },
          ].map((opt) => (
            <button
              key={opt.shape}
              onClick={() => {
                onAddShape(opt.shape);
                setOpen(false);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                padding: '6px 12px',
                background: 'none',
                border: 'none',
                color: '#334155',
                fontSize: 12,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              {opt.icon} {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Divider() {
  return <div style={{ width: 1, height: 20, background: '#e2e8f0', margin: '0 4px' }} />;
}

const floatingBtnStyle: CSSProperties = {
  background: 'transparent',
  border: 'none',
  borderRadius: 6,
  color: '#475569',
  padding: '6px 8px',
  fontSize: 11,
  fontWeight: 500,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
